import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../api/supabase'
import type { InvoiceParsed, InvoicePivotItem } from '../api/claudeInvoiceParser'

export interface CommercialInvoice {
  id: string
  invoice_no: string | null
  job_ref: string | null
  bill_no: string | null
  customer_id: string | null
  shipper: string | null
  shipper_address: string | null
  consignee: string | null
  country_of_origin: string | null
  destination_country: string | null
  invoice_date: string | null
  currency: string
  total_amount: number | null
  gross_weight_kg: number | null
  net_weight_kg: number | null
  packages: number | null
  // Z98: the header figures exactly as printed on the invoice, kept because total_amount / gross_weight_kg /
  // net_weight_kg above are overwritten with the sum of the 2 dp lines. Lets us answer "the invoice says
  // 2519.898, why was 2519.90 declared". null on invoices saved before Z98.
  stated_total_amount: number | null
  stated_gross_weight_kg: number | null
  stated_net_weight_kg: number | null
  incoterms: string | null
  status: string
  source_type: string
  source_file_name: string | null
  ai_confidence: number | null
  vat_deferred: boolean
  use_cyuk_dan: boolean
  port_of_loading: string | null
  port_of_discharge: string | null
  vessel_flight: string | null
  consignee_address: string | null
  drive_folder_url: string | null
  eori: string | null
  vat_no: string | null
  created_at: string
}

// Z42: these field names were wrong against the live table for years -- see the migration
// comment in 20260909e_z42_invoice_items_pcs_ctns.sql. invoice_items' real columns are
// `description` (not goods_description), `gross_weight_kg`/`net_weight_kg` (not gross_weight/
// net_weight), and `total_amount` (not amount) -- this table is shared with the Sage/manual
// billing invoice feature (useInvoices.ts, InvoiceGenerator.tsx), which already uses those real
// names, so THIS code was the one that had drifted. pcs/ctns didn't exist at all until that
// migration added them.
export interface InvoiceItemDB {
  id: string
  invoice_id: string
  line_no: number | null
  hs_code: string | null
  hs_code_suggestion: string | null
  hs_code_confidence: number | null
  hs_code_confirmed: boolean
  description: string | null
  pcs: number | null
  ctns: number | null
  // Z74: raw AI-extracted carton count before customs rounding (see roundCartonsForCustoms) --
  // null when ctns was never adjusted (already a whole number, or never captured).
  ctns_original: number | null
  gross_weight_kg: number | null
  net_weight_kg: number | null
  unit_value: number | null
  total_amount: number | null
  currency: string | null
  country_of_origin: string | null
}

export interface CartonRounding {
  adjusted: number | null
  original: number | null
  wasAdjusted: boolean
}

// Z74: UK Customs requires No. of Cartons >= 1 per real line item. A CI's raw carton count can
// arrive fractional (0.23, 0.15, 0.13 -- seen in Gary's Makera CI, from an upstream unit-of-
// measure split) or, less commonly, a whole number that still needs rounding for some other
// upstream reason -- ceiling is used throughout (not nearest-int) since Gary flagged it as the
// safer default pending Ada's confirmation: rounding DOWN a real carton count risks under-
// declaring cartons to customs, which ceiling can never do.
//
// null is left untouched -- it means "never captured", not "genuinely zero", and forcing it to 1
// would mask a real AI-extraction gap rather than a rounding one.
export function roundCartonsForCustoms(raw: number | null | undefined): CartonRounding {
  if (raw === null || raw === undefined) return { adjusted: null, original: null, wasAdjusted: false }
  if (raw < 0) throw new Error(`Cartons cannot be negative (got ${raw})`)
  const adjusted = Math.max(1, Math.ceil(raw))
  const wasAdjusted = adjusted !== raw
  return { adjusted, original: wasAdjusted ? raw : null, wasAdjusted }
}

export function useCommercialInvoices(search?: string, customerId?: string) {
  const [invoices, setInvoices] = useState<CommercialInvoice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchInvoices = async () => {
    setLoading(true)
    let q = supabase.from('commercial_invoices').select('*').order('created_at', { ascending: false })
    if (search) {
      q = q.or(`invoice_no.ilike.%${search}%,consignee.ilike.%${search}%,shipper.ilike.%${search}%`)
    }
    if (customerId) {
      q = q.eq('customer_id', customerId)
    }
    const { data, error: err } = await q
    if (err) setError(err.message)
    else setInvoices(data || [])
    setLoading(false)
  }

  useEffect(() => { fetchInvoices() }, [search, customerId])

  const stats = {
    total: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    confirmed: invoices.filter(i => i.status === 'confirmed').length,
    filed: invoices.filter(i => i.status === 'filed').length,
    aiParsed: invoices.filter(i => i.source_type === 'pdf').length,
  }

  const createInvoiceFromAI = async (parsed: InvoiceParsed, jobRef?: string, driveFolderUrl?: string, customerId?: string) => {
    // Z74: validated before the header row is even created -- a negative carton count means
    // don't import AT ALL, not "import everything except this one bad row".
    const negativeCtnsLines = parsed.items.filter(item => item.ctns != null && item.ctns < 0).map(item => item.line_no)
    if (negativeCtnsLines.length > 0) {
      throw new Error(`Cannot import: negative cartons on line${negativeCtnsLines.length > 1 ? 's' : ''} ${negativeCtnsLines.join(', ')}. Fix the source CI or correct the value before saving.`)
    }

    const { data: inv, error: invErr } = await supabase
      .from('commercial_invoices')
      .insert({
        invoice_no: parsed.header.invoice_no,
        job_ref: jobRef || parsed.header.job_ref,
        bill_no: parsed.header.bill_no,
        customer_id: customerId || null,
        declare_method: parsed.header.declare_method,
        trade_term: parsed.header.trade_term,
        shipper: parsed.header.shipper,
        shipper_address: parsed.header.shipper_address,
        consignee: parsed.header.consignee,
        consignee_address: parsed.header.consignee_address,
        country_of_origin: parsed.header.country_of_origin,
        destination_country: parsed.header.destination_country,
        invoice_date: parsed.header.invoice_date,
        currency: parsed.header.currency || 'USD',
        total_amount: parsed.header.total_amount,
        gross_weight_kg: parsed.header.gross_weight_kg,
        net_weight_kg: parsed.header.net_weight_kg,
        packages: parsed.header.packages,
        stated_total_amount: parsed.header.stated_total_amount ?? null,
        stated_gross_weight_kg: parsed.header.stated_gross_weight_kg ?? null,
        stated_net_weight_kg: parsed.header.stated_net_weight_kg ?? null,
        incoterms: parsed.header.incoterms,
        vessel_flight: parsed.header.vessel_flight,
        port_of_loading: parsed.header.port_of_loading,
        port_of_discharge: parsed.header.port_of_discharge,
        vat_deferred: parsed.header.vat_deferred || false,
        use_cyuk_dan: parsed.header.use_cyuk_dan || false,
        eori: parsed.header.eori,
        vat_no: parsed.header.vat_no,
        drive_folder_url: driveFolderUrl || null,
        status: 'draft',
        source_type: 'pdf',
        ai_confidence: parsed.confidence,
      })
      .select()
      .single()

    if (invErr) throw new Error(invErr.message)

    // Insert line items
    if (parsed.items.length > 0) {
      const items = parsed.items.map(item => {
        const { adjusted, original } = roundCartonsForCustoms(item.ctns)
        return {
          invoice_id: inv.id,
          line_no: item.line_no,
          hs_code: item.hs_code,
          hs_code_suggestion: item.hs_code_suggestion,
          hs_code_confidence: item.hs_code_confidence,
          hs_code_confirmed: false,
          description: item.goods_description,
          pcs: item.pcs,
          ctns: adjusted,
          ctns_original: item.ctns_original ?? original,   // Z98: keep the editor's 0 -> 1 original
          gross_weight_kg: item.gross_weight,
          net_weight_kg: item.net_weight,
          unit_value: item.unit_value,
          total_amount: item.amount,
          currency: item.currency,
          country_of_origin: item.country_of_origin,
        }
      })
      // Z42: this was a bare fire-and-forget call with no error check -- every one of these
      // inserts has been failing (wrong column names, fixed above) since the feature was built,
      // and nothing ever surfaced that: the invoice header still saved fine, so staff had no
      // sign anything was wrong until they reopened the invoice and found no line items.
      const { error: itemsErr } = await supabase.from('invoice_items').insert(items)
      if (itemsErr) throw new Error(`Invoice saved, but line items failed to save: ${itemsErr.message}`)
    }

    // Insert pivot rows
    if (parsed.pivot && parsed.pivot.length > 0) {
      // Z74: parsed.pivot's own ctns sums are pre-rounding (computed client-side straight off
      // the AI's raw extraction) -- re-sum the CUSTOMS-COMPLIANT rounded ctns per HS code
      // (grouped the same way parsed.pivot already is) so this table can't drift from
      // invoice_items now that rounding happens at save time.
      const adjustedCtnsByHsCode = new Map<string, number>()
      for (const item of parsed.items) {
        const key = item.hs_code || 'UNKNOWN'
        const { adjusted } = roundCartonsForCustoms(item.ctns)
        adjustedCtnsByHsCode.set(key, (adjustedCtnsByHsCode.get(key) || 0) + (adjusted || 0))
      }
      const pivotRows = parsed.pivot.map((p: InvoicePivotItem) => ({
        invoice_id: inv.id,
        hs_code: p.hs_code,
        goods_description: p.goods_description,
        total_pcs: p.pcs,
        total_ctns: adjustedCtnsByHsCode.get(p.hs_code || 'UNKNOWN') ?? p.ctns,
        total_gross_weight: p.gross_weight,
        total_net_weight: p.net_weight,
        total_amount: p.amount,
      }))
      await supabase.from('invoice_pivot').insert(pivotRows)
    }

    await fetchInvoices()
    return inv
  }

  const updateInvoice = async (id: string, updates: Partial<CommercialInvoice>) => {
    const { error: err } = await supabase.from('commercial_invoices').update(updates).eq('id', id)
    if (err) throw new Error(err.message)
    await fetchInvoices()
  }

  // Z42: generalizes updateItemHSCode to any editable line-item field -- InvoiceDetail.tsx
  // (the post-save view) previously could only edit hs_code, so a mis-parsed description/
  // weight/quantity had no way to be corrected once the invoice was saved, short of deleting
  // and re-uploading the whole invoice.
  const updateInvoiceItem = async (itemId: string, updates: Partial<InvoiceItemDB>) => {
    const { error: err } = await supabase.from('invoice_items').update(updates).eq('id', itemId)
    if (err) throw new Error(err.message)
  }

  // Z73: eCustoms declaration data source -- every commercial_invoices header for this job_ref,
  // each with its own invoice_items, rather than a separate commercial_invoice_lines table (this
  // job's CI line items already live here, just spread across 1+ invoices).
  const getInvoiceItemsByJobRef = async (jobRef: string): Promise<{ invoice: CommercialInvoice; items: InvoiceItemDB[] }[]> => {
    const { data: invoicesForJob, error: invErr } = await supabase
      .from('commercial_invoices')
      .select('*')
      .eq('job_ref', jobRef)
    if (invErr) throw new Error(invErr.message)
    if (!invoicesForJob || invoicesForJob.length === 0) return []

    const { data: allItems, error: itemsErr } = await supabase
      .from('invoice_items')
      .select('*')
      .in('invoice_id', invoicesForJob.map(i => i.id))
      .order('line_no')
    if (itemsErr) throw new Error(itemsErr.message)

    return invoicesForJob.map(invoice => ({
      invoice,
      items: (allItems || []).filter(it => it.invoice_id === invoice.id),
    }))
  }

  const updateItemHSCode = async (itemId: string, hsCode: string, description: string) => {
    await supabase.from('invoice_items').update({
      hs_code: hsCode, hs_code_confirmed: true
    }).eq('id', itemId)

    await supabase.from('hs_code_memory').upsert({
      description, hs_code: hsCode, confidence: 1.0,
      usage_count: 1, last_used_at: new Date().toISOString()
    }, { onConflict: 'description_normalized,hs_code' })
  }

  const lookupHSCodeMemory = async (description: string) => {
    const { data } = await supabase
      .from('hs_code_memory')
      .select('hs_code,confidence,usage_count')
      .ilike('description_normalized', `%${description.toLowerCase()}%`)
      .order('usage_count', { ascending: false })
      .limit(5)
    return data || []
  }

  const getInvoiceWithItems = useCallback(async (id: string) => {
    const { data: inv } = await supabase.from('commercial_invoices').select('*').eq('id', id).single()
    const { data: items } = await supabase.from('invoice_items').select('*').eq('invoice_id', id).order('line_no')
    const { data: pivot } = await supabase.from('invoice_pivot').select('*').eq('invoice_id', id)
    return { invoice: inv, items: items || [], pivot: pivot || [] }
  }, [])

  const deleteInvoice = async (id: string): Promise<void> => {
    // Cascade delete: items and pivot rows first, then header
    await supabase.from('invoice_items').delete().eq('invoice_id', id)
    await supabase.from('invoice_pivot').delete().eq('invoice_id', id)
    const { error: err } = await supabase.from('commercial_invoices').delete().eq('id', id)
    if (err) throw new Error(err.message)
    await fetchInvoices()
  }

  const checkDuplicateInvoice = async (invoiceNo: string): Promise<CommercialInvoice | null> => {
    if (!invoiceNo) return null
    const { data } = await supabase
      .from('commercial_invoices')
      .select('*')
      .eq('invoice_no', invoiceNo)
      .single()
    return data || null
  }

  return { invoices, loading, error, stats, createInvoiceFromAI, updateInvoice, updateItemHSCode, updateInvoiceItem, lookupHSCodeMemory, getInvoiceWithItems, getInvoiceItemsByJobRef, deleteInvoice, checkDuplicateInvoice, refetch: fetchInvoices }
}
